import { inject, Injectable } from '@angular/core';
import { DoubleBackendService } from '@dev-fast/backend-services';
import { type IDoubleTripleJackpot, type IDoubleTripleSequenceEvent, type IMessage, EWalletType } from '@dev-fast/types';
import { type NgxsOnInit, type StateContext, Action, Selector, State } from '@ngxs/store';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';

import { getJackpotStatusAndSendMessageToChatAdapter } from './adapters/double';
import { createTipleJackpotChatMessage } from './adapters/double/create-triple-jackpot-chat-message';
import { getChatHistoryMessagesAdapter } from './adapters/double/get-chat-history-messages.adapter';
import { AddDoubleMessages, GetDoubleJackpotStatusAndSendMessageToChat, GetDoubleMessages } from './game-messages.actions';
import type { GameMessagesStateModel } from './game-messages.model';
import { GAME_MESSAGES_INITIAL_STATE } from './game-messages.model';
import type { IDoubleTripleJackpotStatus, IGetDoubleJackpotHistory } from './types';

const GAME_MESSAGES_LIMIT = 50;

@State<GameMessagesStateModel>({
  name: 'gameMessages',
  defaults: GAME_MESSAGES_INITIAL_STATE,
})
@Injectable()
export class GameMessagesState implements NgxsOnInit {
  readonly #doubleBackendService = inject(DoubleBackendService);

  /**
   * Selector: Сообщения Double
   * */
  @Selector()
  static doubleMessages(state: GameMessagesStateModel): IMessage[] {
    return state.doubleMessages;
  }

  ngxsOnInit(ctx: StateContext<GameMessagesStateModel>): void {
    this.#configureDouble(ctx);
  }

  /**
   * Action: Отправить сообщение Double в чат
   * */
  @Action(AddDoubleMessages)
  doubleAddChatMessage({ getState, patchState }: StateContext<GameMessagesStateModel>, { messages }: AddDoubleMessages): void {
    patchState({
      doubleMessages: [...getState().doubleMessages, ...messages].slice(-GAME_MESSAGES_LIMIT),
    });
  }

  /**
   * Action: Получить историю сообщений Double
   * */
  @Action(GetDoubleMessages)
  getChatHistoryMessages(
    { dispatch }: StateContext<GameMessagesStateModel>,
    { walletType }: GetDoubleMessages,
  ): Observable<IGetDoubleJackpotHistory> {
    return this.#doubleBackendService.getDoubleJackpotHistory({ number: 1, size: 1 }, walletType).pipe(
      tap((response) => {
        const message = getChatHistoryMessagesAdapter(response, walletType);
        if (message) {
          dispatch([new AddDoubleMessages([message])]);
        }
      }),
    );
  }

  /**
   * Action: Получить статус джекпота Double и отправить сообщение в чат
   * */
  @Action(GetDoubleJackpotStatusAndSendMessageToChat)
  getJackpotStatusAndSendMessageToChat(
    { dispatch }: StateContext<GameMessagesStateModel>,
    { walletType }: GetDoubleJackpotStatusAndSendMessageToChat,
  ): Observable<IDoubleTripleJackpotStatus> {
    return this.#doubleBackendService.getDoubleJackpotStatus(walletType).pipe(
      tap((response) => {
        const message = getJackpotStatusAndSendMessageToChatAdapter(response, walletType);
        dispatch([new AddDoubleMessages([message])]);
      }),
    );
  }

  /**
   * Инициализация Double
   * */
  #configureDouble(ctx: StateContext<GameMessagesStateModel>): void {
    this.#doubleBackendService
      .watchJackpot()
      .pipe(tap((update) => this.#handleJackpot(ctx, update)))
      .subscribe();

    this.#doubleBackendService
      .watchTripleSequence()
      .pipe(tap((update) => this.#handleTripleSequenceUpdate(ctx, update)))
      .subscribe();
  }

  /**
   * Реакция на трипл джекпот в Double
   * */
  #handleJackpot(ctx: StateContext<GameMessagesStateModel>, update: IDoubleTripleJackpot): void {
    const message = createTipleJackpotChatMessage({
      amount: update.amount,
      winnersCount: update.uniqueWinners,
      walletType: update.walletType,
    });
    ctx.dispatch([new AddDoubleMessages([message])]);
  }

  /**
   * Реакция на 2/3 в трипл джекпоте в Double
   * */
  #handleTripleSequenceUpdate(ctx: StateContext<GameMessagesStateModel>, update: Partial<IDoubleTripleSequenceEvent>): void {
    if (update?.sequence?.length === 2) {
      ctx.dispatch([new GetDoubleJackpotStatusAndSendMessageToChat(update.walletType ?? EWalletType.REAL)]);
    }
  }
}
